import toast from 'react-hot-toast'
import {
  CheckIcon,
  CloseIcon,
  IndeterminateIcon,
  UnCheckIcon,
} from '@/components/icons'
import notificationApi from '@/api/admin/notificationApi'
import { AlertMethod } from '@/types/notification'
import { LocationStationFiltered } from '@/types/program'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { FormProvider, useController, useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthContext } from '@/features/auth/context'
import classes from './styles.module.scss'
import useTranslation from '@hooks/useTranslation'

const TAG_MAP = [
  { id: 1, nameKey: 'alertTagWaterEngine', tag: 'Water' },
  { id: 2, nameKey: 'alertTagProgramStateChange', tag: 'ProgramStateChange' },
  { id: 3, nameKey: 'alertTagProgramIssues', tag: 'ProgramIssues' },
  { id: 4, nameKey: 'alertTagStationIssues', tag: 'StationIssues' },
  { id: 5, nameKey: 'alertTagHardware', tag: 'Hardware' },
  { id: 6, nameKey: 'alertTagHardwareIssues', tag: 'HardwareIssues' },
  { id: 7, nameKey: 'alertTagWaterEngineIssues', tag: 'WaterEngineIssues' },
  { id: 8, nameKey: 'alertTagTankLevel', tag: 'WaterTankLevel' },
]

export interface IFormAlerts {
  alerts: {
    id: number | undefined
    hidden: boolean
  }[]
}

interface ListSelectAlertsProps {
  name?: 'alerts'
  id?: string
  handleCloseUser: () => void
  email?: string
}

const ListSelectAlerts = ({
  id,
  name = 'alerts',
  handleCloseUser,
  email,
}: ListSelectAlertsProps) => {
  const { interfaceId } = useAuthContext()
  const form = useForm<IFormAlerts>()
  const { t } = useTranslation()

  const {
    field: { value, onChange },
    // formState: { errors },
  } = useController({ name, control: form.control })

  const { data } = useQuery(
    ['subscription', interfaceId, email],
    async () => {
      if (!email || !interfaceId) return null
      const response = await notificationApi.getSubscription(
        String(interfaceId),
        email,
      )
      return response.data
    },
    { enabled: !!email && !!interfaceId },
  )

  const [enableAlert, setEnableAlert] = useState<boolean>(false)

  const { mutate: updateSubscription, isPending: loadingUpdate } = useMutation(
    async (payload: { tags: string[]; method: AlertMethod }) => {
      if (!email || !interfaceId)
        throw new Error('Missing email or interfaceId')

      return await notificationApi.createOrUpdateSubscription({
        email: email,
        tags: payload.tags,
        method: payload.method,
        objectId: String(interfaceId),
      })
    },
  )

  useEffect(() => {
    if (!!data) {
      const selectedTags = data.tags || []
      const selectedItems = TAG_MAP.filter((item) =>
        selectedTags.includes(item.tag),
      ).map((item) => ({
        id: item.id,
        hidden: false,
      }))

      form.reset({
        alerts: selectedItems,
      })
      setEnableAlert(data?.method === AlertMethod.SMS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const isSelected = (alertsId: number) =>
    !!value?.find((it: any) => it?.id === alertsId)

  const onChangeAlert = (item: LocationStationFiltered) => {
    let _value = [...(value || [])]
    if (isSelected(item.id)) {
      _value = _value.filter((it) => it.id !== item.id)
    } else {
      // Provide a default value for 'name'
      const newItem = {
        id: item.id,
        hidden: false,
      }
      _value.push(newItem)
    }
    onChange(_value)
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setEnableAlert(checked)
  }

  const handleSave = (data: any) => {
    const selectedTags = data.alerts
      .map((item: any) => TAG_MAP.find((tag) => tag.id === item.id)?.tag)
      .filter(Boolean)

    updateSubscription(
      {
        tags: selectedTags,
        method: enableAlert ? AlertMethod.SMS : AlertMethod.Email,
      },
      {
        onSuccess: () => {
          handleCloseUser()
          toast.success('Data saved successfully')
        },
        onError: (error) => {
          toast.error('Data saved failed')
          console.error('Error updating subscription:', error)
        },
      },
    )
  }

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <Stack className={classes.section_alert} direction="row" spacing={3}>
          <CloseIcon onClick={handleCloseUser} sx={{ color: 'text.main' }} />
          <Typography className={classes.txtAlert}>
            {t('alertNotification')}
          </Typography>
        </Stack>
        {loading || loadingUpdate ? (
          <Stack className={classes.groupAlert}>
            <Stack className={classes.circularProgress}>
              <CircularProgress />
            </Stack>
          </Stack>
        ) : (
          <Stack className={classes.groupAlert}>
            <Stack direction="row" className={classes.enableEmail}>
              <Checkbox
                sx={{ p: '0px', color: 'text.main' }}
                checkedIcon={<CheckIcon sx={{ color: '#004744' }} />}
                icon={<UnCheckIcon />}
                indeterminateIcon={
                  <IndeterminateIcon
                    sx={{
                      height: '12.67px',
                      width: '12px',
                    }}
                  />
                }
                checked={enableAlert}
                onChange={handleCheckboxChange}
              />
              <Typography className={classes.txtEmailNotification}>
                {t('enableEmailNotification')}
              </Typography>
            </Stack>
            <Typography className={classes.txtSendEmail}>
              {t('emailNotificationForAlertsWillBeSentToYourEmailAt', {
                email: email || '',
              })}
            </Typography>
            <Typography className={classes.txtAlertType}>
              {t('selectAlertTypeToReceiveTheNotification')}
            </Typography>

            <Stack className={classes.groupSelectAlerts}>
              {TAG_MAP &&
                TAG_MAP.map((item, index) => (
                  <Stack key={index}>
                    <StationItem
                      key={item.id}
                      item={item}
                      isSelected={isSelected}
                      onChangeAlert={onChangeAlert}
                    />
                  </Stack>
                ))}
            </Stack>

            <Stack
              mt="20px"
              justifyContent="flex-end"
              direction="row"
              spacing={1}
              pb={'10px'}
            >
              <Button
                variant="secondary"
                className={classes.btnCancel}
                onClick={handleCloseUser}
              >
                {t('commonActionsCancel')}
              </Button>
              <LoadingButton
                loading={loadingUpdate}
                variant="primary"
                className={classes.btnSave}
                type="submit"
              >
                {t('commonActionsSave')}
              </LoadingButton>
            </Stack>
          </Stack>
        )}
      </form>
    </FormProvider>
  )
}

export default ListSelectAlerts

interface StationItemProps {
  item: any
  isSelected: (id: any) => boolean
  onChangeAlert: (id: any) => void
}

const StationItem = ({ item, isSelected, onChangeAlert }: StationItemProps) => {
  const { t } = useTranslation()
  return (
    <Box
      p="8px"
      sx={{
        ...(isSelected(item.id) && {
          backgroundColor: 'background.overrideDefault',
        }),
      }}
    >
      <FormControlLabel
        label={t(item.nameKey)}
        control={
          <Checkbox
            checked={isSelected(item.id)}
            onChange={() => onChangeAlert(item)}
            sx={{
              padding: 0,
              color: 'text.main',
              margin: '0px 11px 2px 11px',
              '& .MuiSvgIcon-root': {
                fontSize: 16,
              },
            }}
            checkedIcon={<CheckIcon sx={{ color: '#004744' }} />}
            icon={<UnCheckIcon />}
            indeterminateIcon={
              <IndeterminateIcon
                sx={{
                  height: '12.67px',
                  width: '12px',
                }}
              />
            }
          />
        }
        sx={{
          '& .MuiTypography-root': {
            color: 'text.main',
            lineHeight: '100%',
          },
        }}
      />
    </Box>
  )
}
